import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Article, Brain, CalendarBlank, ChartBar, Check, CirclesThreePlus,
  Crown, FilePdf, FilmStrip, Images, Lightning, Palette, PlayCircle, Buildings,
  Question, Sparkle, UsersThree
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import LandingMotionShowcase from './landing/LandingMotionShowcase';
import { LandingWorkflowStory } from './landing/LandingStoryMotion';

const PLAN_ORDER = ['free', 'creator', 'strategist'];
const PLAN_META = {
  free: { amount: 0, icon: Sparkle },
  creator: { amount: 25, icon: Lightning },
  strategist: { amount: 49, icon: Crown },
};
const COMMERCIAL_COPY = {
  it: { monthly:'Mensile', annual:'Annuale', discount:'2 mesi gratis · -17%', perYear:'/anno', annualTotal:'Fatturati annualmente', annualPending:'Abbonamento annuale in attivazione', contact:'Richiedi una demo', agencyLine:'La tua agenzia. Il tuo brand. Tutta la potenza di Sketchario.', agencyDescription:'White Label e gestione agenzia su preventivo. Soluzioni personalizzate, soggette a valutazione.', agencyFeatures:['Brand personalizzato · Powered by Sketchario','Gestione multi-cliente e team su misura','Portale approvazioni e report brandizzati su progetto'], annualBenefit:'Capacità superiore nel piano annuale' },
  en: { monthly:'Monthly', annual:'Yearly', discount:'2 months free · -17%', perYear:'/year', annualTotal:'Billed annually', annualPending:'Annual subscription coming soon', contact:'Request a demo', agencyLine:'Your agency. Your brand. All the power of Sketchario.', agencyDescription:'White-label and agency management tailored to your needs. Custom capabilities subject to scoping.', agencyFeatures:['Custom branding · Powered by Sketchario','Tailored multi-client and team management','Approval portal and branded reports by arrangement'], annualBenefit:'More capacity with an annual plan' },
  es: { monthly:'Mensual', annual:'Anual', discount:'2 meses gratis · -17%', perYear:'/año', annualTotal:'Facturación anual', annualPending:'Suscripción anual próximamente', contact:'Solicitar demo', agencyLine:'Tu agencia. Tu marca. Todo el poder de Sketchario.', agencyDescription:'Marca blanca y gestión de agencia a medida, sujetas a evaluación.', agencyFeatures:['Marca propia · Powered by Sketchario','Gestión de clientes y equipos a medida','Portal de aprobación e informes personalizados bajo propuesta'], annualBenefit:'Más capacidad con el plan anual' },
  fr: { monthly:'Mensuel', annual:'Annuel', discount:'2 mois offerts · -17 %', perYear:'/an', annualTotal:'Facturation annuelle', annualPending:'Abonnement annuel bientôt disponible', contact:'Demander une démo', agencyLine:'Votre agence. Votre marque. Toute la puissance de Sketchario.', agencyDescription:'Marque blanche et gestion agence sur devis, selon les besoins.', agencyFeatures:['Marque personnalisée · Powered by Sketchario','Gestion multi-clients et équipe sur mesure','Portail de validation et rapports personnalisés sur devis'], annualBenefit:'Plus de capacité avec le forfait annuel' },
};
const FEATURE_ICONS = [UsersThree, Palette, Article, Images, FilePdf, CalendarBlank, FilmStrip, ChartBar, Question];
const PROBLEM_ICONS = [Brain, Palette, CirclesThreePlus];
const LANGUAGE_OPTIONS = [
  ['it', 'IT'],
  ['en', 'EN'],
  ['es', 'ES'],
  ['fr', 'FR'],
];
const ITALIAN_JOINERS = /\b(a|e|o|i|il|lo|la|gli|le|un|uno|una|di|da|in|su|per|tra|fra|con|del|dello|della|dei|degli|delle|al|allo|alla|ai|agli|alle|nel|nello|nella|nei|negli|nelle|sul|sullo|sulla|sui|sugli|sulle)\s+(?=\S)/gi;

function protectItalian(text) {
  return typeof text === 'string' ? text.replace(ITALIAN_JOINERS, '$1\u00A0') : text;
}

function jumpTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="group rounded-2xl border border-white/[.08] bg-white/[.025] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-white/[.14] hover:bg-white/[.04] md:p-6">
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl border border-fuchsia-400/20 bg-fuchsia-400/10 text-fuchsia-300 transition-transform duration-300 group-hover:scale-105">
        <Icon size={20} weight="duotone" />
      </div>
      <h3 className="text-base font-semibold text-white" style={{ textWrap: 'balance' }}>{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-400">{body}</p>
    </div>
  );
}

function FunnyCarouselFanShowcase({ copy, protect }) {
  const positions = [
    { x: -118, r: -14, y: 16 },
    { x: -58, r: -7, y: 5 },
    { x: 0, r: 0, y: 0 },
    { x: 58, r: 7, y: 5 },
    { x: 118, r: 14, y: 16 },
  ];
  const cards = Array.isArray(copy?.cards) ? copy.cards : [];

  return (
    <div className="relative mx-auto mt-14 max-w-6xl overflow-hidden rounded-[2rem] border border-white/[.07] bg-gradient-to-br from-indigo-500/[.06] via-[#0d1527] to-fuchsia-500/[.06] px-4 py-8 md:px-8 md:py-10" data-testid="funny-carousel-fan">
      <div className="absolute left-1/2 top-1/2 h-56 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-fuchsia-500/10 blur-3xl" />
      <div className="relative grid items-center gap-8 lg:grid-cols-[.7fr_1.3fr]">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[.2em] text-fuchsia-300">{protect(copy?.eyebrow)}</p>
          <p className="mt-3 text-2xl font-black tracking-[-.03em] md:text-3xl" style={{ textWrap: 'balance' }}>{protect(copy?.title)}</p>
          <p className="mt-3 max-w-md text-sm leading-6 text-slate-400">{protect(copy?.body)}</p>
          <div className="mt-5 flex flex-wrap gap-2 text-[10px] text-slate-400">
            {['Carousel', 'Reel', 'Prompted Reel', 'PDF'].map(label => (
              <span key={label} className="rounded-full border border-white/[.08] bg-white/[.03] px-3 py-1.5">{label}</span>
            ))}
          </div>
        </div>
        <div className="flex min-h-[380px] items-center justify-center overflow-hidden">
          <div className="relative mx-auto h-[390px] w-[520px] max-w-full" aria-hidden="true">
            {cards.slice(0, 5).map((title, index) => {
              const number = String(index + 1).padStart(2, '0');
              const pose = positions[index];
              return (
                <div
                  key={number}
                  className="absolute left-1/2 top-1/2 h-[330px] w-[264px] overflow-hidden rounded-xl border border-black/10 bg-gradient-to-br from-[#f8f4ee] to-[#ddd5cc] p-5 text-[#171717] shadow-2xl shadow-black/30"
                  style={{
                    transform: `translate(-50%, -50%) translate3d(${pose.x}px, ${pose.y}px, 0) rotate(${pose.r}deg)`,
                    zIndex: index === 2 ? 10 : 5 - Math.abs(index - 2),
                  }}
                >
                  <p className="text-[8px] font-bold uppercase tracking-[.18em] text-[#8a4f83]">{protect(copy?.cardEyebrow)}</p>
                  <p className="mt-9 text-2xl font-black leading-[.96] tracking-[-.045em]" style={{ textWrap: 'balance' }}>{protect(title)}</p>
                  <div className="mt-5 h-px w-10 bg-[#8a4f83]/60" />
                  <p className="mt-4 text-[9px] leading-4 text-black/50">{protect(copy?.cardBody)}</p>
                  <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between border-t border-black/10 pt-3 text-[7px] font-semibold uppercase tracking-[.12em] text-black/35">
                    <span>Sketchario</span><span>{number}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FunnyPublicLanding() {
  const { api } = useAuth();
  const { t, i18n } = useTranslation('translation');
  const [checkoutPlan, setCheckoutPlan] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [checkoutError, setCheckoutError] = useState('');
  const language = (i18n.resolvedLanguage || i18n.language || 'it').split('-')[0];
  const copy = t('funnyLanding', { returnObjects: true });
  const commercial = COMMERCIAL_COPY[language] || COMMERCIAL_COPY.it;
  const protect = value => language === 'it' ? protectItalian(value) : value;

  const plans = PLAN_ORDER.map(id => {
    const rawFeatures = t(`landing.pricing.${id}.features`, { returnObjects: true });
    const funnyLine = copy?.pricing?.[`${id}Line`];
    return {
      id,
      ...PLAN_META[id],
      funnyLine: protect(funnyLine),
      name: protect(t(`landing.pricing.${id}.name`)),
      eyebrow: protect(t(`landing.pricing.${id}.eyebrow`)),
      description: protect(t(`landing.pricing.${id}.description`)),
      features: Array.isArray(rawFeatures) ? rawFeatures.map(protect) : rawFeatures,
    };
  });

  const workflowSteps = (Array.isArray(copy?.workflow?.steps) ? copy.workflow.steps : []).map((step, index) => ({
    number: String(index + 1).padStart(2, '0'),
    title: protect(step.title),
    body: protect(step.body),
  }));
  const problemCards = Array.isArray(copy?.problem?.cards) ? copy.problem.cards : [];
  const featureItems = Array.isArray(copy?.features?.items) ? copy.features.items : [];

  useEffect(() => {
    const previousTitle = document.title;
    document.title = copy?.metaTitle || 'Sketchario';
    return () => {
      document.title = previousTitle;
    };
  }, [copy?.metaTitle]);

  const startFree = () => {
    window.location.href = '/login?mode=register';
  };

  const startPaid = async planId => {
    if (checkoutPlan) return;
    setCheckoutPlan(planId);
    setCheckoutError('');
    try {
      const { data } = await api.post('/public/billing/checkout', {
        plan_id: planId,
        billing_period: billingPeriod,
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error('checkout_url_missing');
      window.location.href = data.url;
    } catch (error) {
      const detail = error.response?.data?.detail;
      setCheckoutError(typeof detail === 'string' ? detail : t('landing.pricing.checkoutError'));
      setCheckoutPlan('');
    }
  };

  return (
    <div className="min-h-screen bg-[#080d18] text-white" data-testid="funny-public-landing" style={{ textWrap: 'pretty' }}>
      <header className="sticky top-0 z-50 border-b border-white/[.06] bg-[#080d18]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-6">
          <a href="/" className="flex items-center gap-2.5" aria-label="Sketchario">
            <img src="/assets/LOGO-sketchario.jpg" alt="Sketchario" className="h-9 w-auto rounded-lg object-contain" />
          </a>
          <nav className="hidden items-center gap-6 text-xs text-slate-400 md:flex">
            <button type="button" onClick={() => jumpTo('workflow')} className="hover:text-white">{protect(copy?.nav?.workflow)}</button>
            <button type="button" onClick={() => jumpTo('features')} className="hover:text-white">{protect(copy?.nav?.features)}</button>
            <button type="button" onClick={() => jumpTo('pricing')} className="hover:text-white">{protect(copy?.nav?.pricing)}</button>
          </nav>
          <div className="flex items-center gap-2">
            <select
              aria-label="Language"
              value={language}
              onChange={event => i18n.changeLanguage(event.target.value)}
              className="rounded-lg border border-white/10 bg-white/[.04] px-2 py-2 text-[10px] font-bold text-slate-300 outline-none hover:bg-white/[.07]"
            >
              {LANGUAGE_OPTIONS.map(([code, label]) => <option key={code} value={code} className="bg-[#0d1527]">{label}</option>)}
            </select>
            <a href="/login" className="rounded-xl px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-white/[.05] hover:text-white">{protect(copy?.nav?.login)}</a>
            <button type="button" onClick={startFree} className="hidden rounded-xl bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-3.5 py-2 text-xs font-semibold shadow-lg shadow-fuchsia-950/30 sm:block">{protect(copy?.nav?.start)}</button>
          </div>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden px-4 pb-20 pt-20 md:px-6 md:pb-28 md:pt-28">
          <div className="absolute left-1/2 top-0 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-fuchsia-500/[.08] blur-[120px]" />
          <div className="relative mx-auto max-w-7xl">
            <div className="mx-auto max-w-5xl text-center">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-fuchsia-400/20 bg-fuchsia-400/[.07] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[.18em] text-fuchsia-300">
                <Sparkle size={13} weight="fill" /> {protect(copy?.hero?.eyebrow)}
              </div>
              <h1 className="text-balance text-4xl font-black leading-[1.02] tracking-[-.04em] sm:text-5xl md:text-7xl">
                {protect(copy?.hero?.title)} <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">{protect(copy?.hero?.accent)}</span>
              </h1>
              <p className="mx-auto mt-6 max-w-3xl text-base leading-7 text-slate-400 md:text-lg md:leading-8">{protect(copy?.hero?.subtitle)}</p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <button type="button" onClick={startFree} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-5 py-3 text-sm font-bold shadow-xl shadow-fuchsia-950/30">
                  {protect(copy?.hero?.primary)} <ArrowRight size={16} weight="bold" />
                </button>
                <button type="button" onClick={() => jumpTo('workflow')} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[.03] px-5 py-3 text-sm font-semibold text-slate-200 hover:bg-white/[.06]">
                  <PlayCircle size={17} /> {protect(copy?.hero?.secondary)}
                </button>
              </div>
              <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] text-slate-500">
                {[copy?.hero?.proof1, copy?.hero?.proof2, copy?.hero?.proof3].map(item => (
                  <span key={item} className="flex items-center gap-1.5"><Check size={13} className="text-emerald-400" /> {protect(item)}</span>
                ))}
              </div>
            </div>

            <div className="mt-14 md:mt-20">
              <LandingMotionShowcase />
            </div>
          </div>
        </section>

        <section className="border-y border-white/[.06] bg-white/[.015] px-4 py-16 md:px-6 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-center">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[.2em] text-indigo-300">{protect(copy?.problem?.eyebrow)}</p>
              <h2 className="mt-3 text-3xl font-black tracking-[-.03em] md:text-4xl" style={{ textWrap: 'balance' }}>{protect(copy?.problem?.title)}</h2>
              <p className="mt-4 max-w-xl text-sm leading-7 text-slate-400 md:text-base">{protect(copy?.problem?.body)}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {problemCards.slice(0, 3).map((item, index) => {
                const Icon = PROBLEM_ICONS[index];
                return (
                  <div key={item.title} className="group rounded-2xl border border-white/[.07] bg-[#0d1527] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-white/[.13] hover:bg-[#101a2d]">
                    <Icon size={21} className="text-fuchsia-300 transition-transform duration-300 group-hover:scale-110" />
                    <p className="mt-5 text-sm font-semibold" style={{ textWrap: 'balance' }}>{protect(item.title)}</p>
                    <p className="mt-2 text-xs leading-5 text-slate-500">{protect(item.body)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="workflow" className="scroll-mt-24 px-4 py-20 md:px-6 md:py-28">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-[10px] font-bold uppercase tracking-[.2em] text-fuchsia-300">{protect(copy?.workflow?.eyebrow)}</p>
              <h2 className="mt-3 text-3xl font-black tracking-[-.03em] md:text-4xl" style={{ textWrap: 'balance' }}>{protect(copy?.workflow?.title)}</h2>
              <p className="mt-4 text-sm leading-7 text-slate-400 md:text-base">{protect(copy?.workflow?.body)}</p>
            </div>
            <LandingWorkflowStory steps={workflowSteps} />
            <FunnyCarouselFanShowcase copy={copy?.carouselDemo} protect={protect} />
          </div>
        </section>

        <section className="px-4 pb-20 md:px-6 md:pb-28">
          <div className="mx-auto max-w-7xl rounded-[2rem] border border-fuchsia-400/15 bg-gradient-to-br from-indigo-500/[.08] via-white/[.02] to-fuchsia-500/[.08] p-6 md:p-10">
            <p className="text-[10px] font-bold uppercase tracking-[.2em] text-fuchsia-300">{protect(copy?.ai?.eyebrow)}</p>
            <h2 className="mt-3 max-w-5xl text-3xl font-black tracking-[-.03em] md:text-5xl" style={{ textWrap: 'balance' }}>{protect(copy?.ai?.title)}</h2>
            <p className="mt-5 max-w-3xl text-sm leading-7 text-slate-400 md:text-base">{protect(copy?.ai?.body)}</p>
          </div>
        </section>

        <section id="features" className="scroll-mt-24 border-y border-white/[.06] bg-white/[.015] px-4 py-20 md:px-6 md:py-28">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-[10px] font-bold uppercase tracking-[.2em] text-indigo-300">{protect(copy?.features?.eyebrow)}</p>
              <h2 className="mt-3 text-3xl font-black tracking-[-.03em] md:text-4xl" style={{ textWrap: 'balance' }}>{protect(copy?.features?.title)}</h2>
              <p className="mt-4 text-sm leading-7 text-slate-400 md:text-base">{protect(copy?.features?.body)}</p>
            </div>
            <div className="mt-10 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {featureItems.slice(0, FEATURE_ICONS.length).map((item, index) => (
                <FeatureCard key={item.title} icon={FEATURE_ICONS[index]} title={protect(item.title)} body={protect(item.body)} />
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="scroll-mt-24 px-4 py-20 md:px-6 md:py-28">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-[10px] font-bold uppercase tracking-[.2em] text-fuchsia-300">{protect(copy?.pricing?.eyebrow)}</p>
              <h2 className="mt-3 text-3xl font-black tracking-[-.03em] md:text-4xl" style={{ textWrap: 'balance' }}>{protect(copy?.pricing?.title)}</h2>
              <p className="mt-4 text-sm leading-7 text-slate-400 md:text-base">{protect(copy?.pricing?.body)}</p>
            </div>

            <div className="mt-7 flex flex-wrap items-center justify-center gap-2" role="group" aria-label="Billing period">
              <button type="button" aria-pressed={billingPeriod === 'monthly'} onClick={() => setBillingPeriod('monthly')} className={`rounded-xl px-5 py-2 text-sm font-semibold ${billingPeriod === 'monthly' ? 'bg-white text-slate-950' : 'border border-white/20 text-slate-200'}`}>{commercial.monthly}</button>
              <button type="button" aria-pressed={billingPeriod === 'annual'} onClick={() => setBillingPeriod('annual')} className={`rounded-xl px-5 py-2 text-sm font-semibold ${billingPeriod === 'annual' ? 'bg-white text-slate-950' : 'border border-white/20 text-slate-200'}`}>{commercial.annual} · {commercial.discount}</button>
            </div>
            <div className="mt-10 grid items-stretch gap-4 md:grid-cols-2 xl:grid-cols-4">
              {plans.map(plan => {
                const Icon = plan.icon;
                const featured = plan.id === 'strategist';
                const cta = plan.id === 'free'
                  ? copy?.pricing?.freeCta
                  : t('funnyLanding.pricing.paidCta', { plan: plan.name });
                return (
                  <article key={plan.id} className={`relative flex h-full flex-col rounded-[1.75rem] border p-6 md:p-7 ${featured ? 'border-fuchsia-400/30 bg-gradient-to-b from-fuchsia-500/[.08] to-white/[.025]' : 'border-white/[.08] bg-white/[.025]'}`}>
                    {featured && <div className="absolute right-5 top-5 rounded-full bg-fuchsia-400/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[.16em] text-fuchsia-300">{protect(copy?.pricing?.featured)}</div>}
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[.04] text-fuchsia-300"><Icon size={20} weight="duotone" /></div>
                    <p className="mt-5 text-[10px] font-bold uppercase tracking-[.18em] text-slate-500">{plan.eyebrow}</p>
                    <h3 className="mt-1 text-2xl font-black" style={{ textWrap: 'balance' }}>{plan.name}</h3>
                    <div className="mt-4 flex items-end gap-1"><span className="text-4xl font-black">€{billingPeriod === 'annual' && plan.id !== 'free' ? plan.amount * 10 : plan.amount}</span><span className="pb-1 text-sm text-slate-500">{billingPeriod === 'annual' && plan.id !== 'free' ? commercial.perYear : copy?.pricing?.perMonth}</span></div>
                    {billingPeriod === 'annual' && plan.id !== 'free' && <p className="mt-2 text-xs text-emerald-300">{commercial.annualTotal}{plan.id === 'creator' ? ' · 5 progetti (alla disponibilità del piano)' : ''}</p>}
                    <p className="mt-4 min-h-[48px] text-base font-bold leading-6 text-white">{plan.funnyLine}</p>
                    <p className="mt-2 min-h-[72px] text-sm leading-6 text-slate-400">{plan.description}</p>
                    <ul className="mb-7 mt-6 space-y-3">
                      {(Array.isArray(plan.features) ? plan.features : []).map(item => (
                        <li key={item} className="flex gap-2 text-sm text-slate-300"><Check size={15} className="mt-0.5 shrink-0 text-emerald-400" /> <span>{item}</span></li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={() => plan.id === 'free' ? startFree() : startPaid(plan.id)}
                      disabled={Boolean(checkoutPlan)}
                      className={`mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold disabled:opacity-50 ${featured ? 'bg-gradient-to-r from-indigo-500 to-fuchsia-500' : 'border border-white/10 bg-white/[.04] hover:bg-white/[.07]'}`}
                    >
                      {checkoutPlan === plan.id ? t('landing.pricing.redirecting') : protect(cta)} <ArrowRight size={15} />
                    </button>
                  </article>
                );
              })}
              <article className="flex h-full flex-col rounded-[1.75rem] border border-indigo-400/30 bg-gradient-to-b from-indigo-500/[.09] to-white/[.025] p-6 md:p-7">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[.04] text-indigo-300"><Buildings size={20} weight="duotone" /></div>
                <p className="mt-5 text-[10px] font-bold uppercase tracking-[.18em] text-slate-500">BUSINESS</p>
                <h3 className="mt-1 text-2xl font-black">Agency</h3>
                <p className="mt-4 text-2xl font-black">{language === 'it' ? 'Su richiesta' : language === 'fr' ? 'Sur devis' : language === 'es' ? 'A consultar' : 'Custom pricing'}</p>
                <p className="mt-4 text-base font-bold leading-6">{commercial.agencyLine}</p>
                <p className="mt-3 text-sm leading-6 text-slate-400">{commercial.agencyDescription}</p>
                <ul className="mb-7 mt-6 space-y-3">{commercial.agencyFeatures.map(item => <li key={item} className="flex gap-2 text-sm text-slate-300"><Check size={15} className="mt-0.5 shrink-0 text-emerald-400" /><span>{item}</span></li>)}</ul>
                <a href="/contact.html" className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl border border-indigo-400/40 bg-indigo-500/20 px-4 py-3 text-sm font-bold hover:bg-indigo-500/30">{commercial.contact}<ArrowRight size={15}/></a>
              </article>
            </div>
            {checkoutError && <p className="mx-auto mt-4 max-w-2xl text-center text-xs text-red-300">{checkoutError}</p>}
            <p className="mx-auto mt-5 max-w-4xl text-center text-[11px] leading-5 text-slate-600">{protect(copy?.pricing?.note)}</p>
          </div>
        </section>

        <section className="border-t border-white/[.06] bg-white/[.015] px-4 py-20 md:px-6 md:py-28">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-[10px] font-bold uppercase tracking-[.2em] text-fuchsia-300">{protect(copy?.final?.eyebrow)}</p>
            <h2 className="mt-3 text-4xl font-black tracking-[-.04em] md:text-6xl" style={{ textWrap: 'balance' }}>{protect(copy?.final?.title)}</h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-slate-400">{protect(copy?.final?.body)}</p>
            <button type="button" onClick={startFree} className="mt-8 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-6 py-3.5 text-sm font-bold shadow-xl shadow-fuchsia-950/30">
              {protect(copy?.final?.cta)} <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/[.06] px-4 py-8 text-center text-[11px] text-slate-600 md:px-6">
        {protect(copy?.footer)}
      </footer>
    </div>
  );
}
